<template>
  <modal name="securone-edit-user" class="modal-inner" transition="pop-out" :width="620" height="auto" :adaptive="true" :scrollable="true" :focus-trap="true" @before-close="reset">
    <span class="close-button" @click="$modal.hide('securone-edit-user')"><BaseIcon icon="times-circle" class="text-white" /></span>
    <div class="modal-header text-lg font-serif">{{ $t("securone.users.edit_user") }}</div>
    <div class="modal-body" v-if="user">
      <div class="flex items-center gap-3 mb-4">
        <SoAvatar :name="user.name" size="lg" :muted="user.pending" />
        <div>
          <div class="font-semibold text-gray-800">{{ user.name }}</div>
          <div class="text-xs text-gray-500">
            {{ $t("securone.level." + user.level) }}
            <template v-if="user.pending"> · {{ $t("securone.users.invited_pending") }}</template>
            <template v-if="user.lastLogin"> · {{ $t("securone.users.last_online") }} {{ dateTime(user.lastLogin) }}</template>
          </div>
        </div>
      </div>
      <ValidationObserver ref="form">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/2 px-2 py-2"><BaseInput v-model="form.firstname" :field_name="$t('securone.invite.first_name')" rules="required" /></div>
          <div class="w-full md:w-1/2 px-2 py-2"><BaseInput v-model="form.lastname" :field_name="$t('securone.invite.last_name')" /></div>
          <div class="w-full md:w-1/2 px-2 py-2"><BaseInput v-model="form.email" type="email" :field_name="$t('securone.invite.email')" rules="required|email" /></div>
          <div class="w-full md:w-1/2 px-2 py-2"><BaseInput v-model="form.phone" type="tel" :field_name="$t('securone.invite.phone')" /></div>
        </div>
      </ValidationObserver>

      <!-- a basic user's departments are edited here (V7 applies); a department user's in SecurCloud -->
      <div class="mt-3" v-if="user.level === 'basic'">
        <DepartmentPicker v-model="form.departmentIds" :label="$t('securone.department.label')" :help="$t('securone.users.departments_help')" />
      </div>
      <div class="mt-3" v-else>
        <div class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">{{ $t("securone.department.label") }}</div>
        <div class="text-sm text-gray-700">{{ user.departmentNames.join(", ") || "—" }}</div>
        <div class="text-xs text-gray-500 mt-1">{{ $t("securone.users.managed_in_securcloud") }}</div>
      </div>

      <div class="mt-3">
        <div class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">{{ $t("securone.common.groups") }}</div>
        <div class="flex flex-wrap gap-1">
          <SoChip v-for="g in user.groups" :key="g._id">{{ g.name }}</SoChip>
          <span v-if="!user.groups.length" class="text-sm text-gray-400">{{ $t("securone.common.none") }}</span>
        </div>
        <div class="text-xs text-gray-500 mt-2">{{ $t("securone.users.membership_edited_under_groups") }}</div>
      </div>
      <SoNote v-if="user.isMe" tone="muted" class="mt-3">{{ $t("securone.users.cannot_remove_self") }}</SoNote>
      <SoNote v-if="error" tone="bad" class="mt-3">{{ error }}</SoNote>
    </div>
    <div class="modal-footer">
      <div class="footer-container flex items-center gap-2">
        <button v-if="user && user.canRemove" class="sd-btn sd-btn--danger" @click="$emit('remove', user)"><BaseIcon icon="trash" class="mr-1" />{{ $t("securone.users.remove_user") }}</button>
        <span class="flex-1"></span>
        <button class="sd-btn sd-btn--secondary" @click="$modal.hide('securone-edit-user')">{{ $t("securone.common.cancel") }}</button>
        <button class="sd-btn sd-btn--success" :disabled="busy" @click="submit">{{ $t("securone.common.save") }}<BaseIcon icon="save" class="ml-1" /></button>
      </div>
    </div>
    <LossPreviewDialog ref="loss" />
  </modal>
</template>

<script>
import SoAvatar from "./SoAvatar.vue";
import SoChip from "./SoChip.vue";
import SoNote from "./SoNote.vue";
import DepartmentPicker from "./DepartmentPicker.vue";
import LossPreviewDialog from "./LossPreviewDialog.vue";
import api from "../securone.api";
import { errorMessage, dateTime } from "../securone.helpers";

// Removing a department from a basic user drops the memberships that no
// longer satisfy V2. The preview is shown first and the save waits for
// confirmation (V7, C10).
export default {
  name: "EditUserModal",
  components: { SoAvatar, SoChip, SoNote, DepartmentPicker, LossPreviewDialog },
  data() {
    return { user: null, form: { firstname: "", lastname: "", email: "", phone: "", departmentIds: [] }, busy: false, error: null };
  },
  methods: {
    dateTime,
    open(user) {
      this.reset();
      this.user = user;
      this.form = { firstname: user.firstname, lastname: user.lastname, email: user.email, phone: user.phone, departmentIds: (user.departmentIds || []).map(String) };
      this.$modal.show("securone-edit-user");
    },
    reset() {
      this.busy = false;
      this.error = null;
    },
    departmentsChanged() {
      const before = (this.user.departmentIds || []).map(String).sort().join("|");
      return before !== [...this.form.departmentIds].sort().join("|");
    },
    async submit() {
      if (!(await this.$refs.form.validate())) return;
      this.busy = true;
      this.error = null;
      try {
        const body = { firstname: this.form.firstname, lastname: this.form.lastname, email: this.form.email, phone: this.form.phone };
        if (this.user.level === "basic" && this.departmentsChanged()) {
          if (!this.form.departmentIds.length) {
            this.error = this.$t("securone.department.required");
            return;
          }
          const preview = await api.departmentsPreview(this.user._id, this.form.departmentIds);
          if (preview.droppedGroups.length) {
            const confirmed = await this.$refs.loss.open(preview);
            if (!confirmed) return;
          }
          body.departmentIds = this.form.departmentIds;
        }
        const updated = await api.updateUser(this.user._id, body);
        this.$modal.hide("securone-edit-user");
        this.$emit("saved", updated);
      } catch (error) {
        this.error = errorMessage(this, error);
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
